'use client';

import type React from 'react';
import { useState } from 'react';
import { X } from 'lucide-react';
import Link from 'next/link';

import { cn } from '@skybeam/shared-utils/classnames/cn';
import { Button } from '@simulmedia/ui-kit';
import type { TopNavItem, NavbarModeType } from './navbar';
import { NavItem } from './nav-item';
import Logo from '../../../../public/skybeam-logo-with-text-blue.svg';

import { CircleSmall } from 'lucide-react';
import { ChevronDown } from 'lucide-react';

import styles from './navbar.module.css';
import { SignupLinkWithUTM } from '../sign-up-link-with-UTM';

interface NavbarMobileProps {
  navbarMode: NavbarModeType;
  navItems: TopNavItem[];
  setClose: VoidFunction;
}

export const NavbarMobile: React.FC<NavbarMobileProps> = ({
  navbarMode,
  navItems,
  setClose,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex((cur) => (cur === i ? null : i));

  return (
    <>
      <div
        data-test-mobile-navbar
        className={cn(
          styles.navbarMobile,
          'tablet:py-5 z-100 flex-col rounded-b-2xl border-t border-b border-transparent px-10 py-2 transition-colors duration-500',
          navbarMode === 'transparent' ? styles.light : styles[navbarMode],
        )}
      >
        <div className="tablet:h-10 flex h-[34px] items-center justify-between">
          <div className="tablet:gap-x-8 flex h-8 items-center gap-x-4">
            <X className={styles.closeButton} onClick={setClose} />
            <Link href="/">
              <Logo
                id="skybeam-logo"
                className={styles.mobileLogo}
                viewBox="0 0 127 32"
              />
            </Link>
          </div>
          <div className="tablet:flex hidden justify-center gap-x-2">
            <Link
              href={`${process.env.NEXT_PUBLIC_CALENDLY_URL}`}
              className={cn(styles.link)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="ghost"
                className="h-10 px-6 text-base font-semibold hover:no-underline"
              >
                Book a Demo
              </Button>
            </Link>
            <SignupLinkWithUTM>
              <Button
                className={cn(
                  'h-10 rounded-lg px-6 text-base font-semibold',
                  navbarMode === 'dark' ? 'text-white' : 'text-black',
                )}
                variant={navbarMode === 'dark' ? 'primary' : 'secondary'}
              >
                Start Now
              </Button>
            </SignupLinkWithUTM>
          </div>
        </div>

        <div className="flex flex-col pt-5">
          {navItems.map(({ label, link, subItems }, i) => {
            if (link && !subItems) {
              return (
                <NavItem key={link} href={link} onClick={setClose}>
                  {label}
                </NavItem>
              );
            }

            return (
              <div
                key={label}
                className={cn(
                  'border-t border-gray-600 py-3 text-base',
                  styles.navLink,
                )}
                onClick={() => toggle(i)}
              >
                <div className="flex items-center gap-1">
                  {label}
                  <ChevronDown
                    className={cn(
                      styles.arrowDown,
                      'size-4 transition-transform',
                      openIndex === i && 'rotate-180',
                    )}
                  />
                </div>

                {openIndex === i && (
                  <div className="flex flex-col">
                    {subItems?.map(({ icon, label, link }) => (
                      <Link
                        key={label}
                        className="flex items-center gap-x-3 py-3"
                        href={link}
                        onClick={setClose}
                      >
                        <div className="flex size-8 items-center justify-center rounded-sm border border-blue-500 bg-blue-700">
                          {icon}
                        </div>
                        <span>{label}</span>
                        {!link && (
                          <div
                            className={cn(
                              'flex items-center gap-x-0.5 rounded-md py-0.5 pr-2 pl-1 text-sm',
                              navbarMode === 'light'
                                ? 'bg-blue-800'
                                : navbarMode === 'dark' ||
                                    navbarMode === 'transparent'
                                  ? 'bg-blue-950'
                                  : 'bg-blue-100',
                            )}
                          >
                            <CircleSmall
                              className={cn(
                                'size-3',
                                navbarMode === 'white' || navbarMode === 'amber'
                                  ? 'fill-lime-500 text-lime-500'
                                  : 'fill-lime-200 text-lime-200',
                              )}
                            />
                            <span
                              className={cn(
                                'text-white',
                                navbarMode === 'white' || navbarMode === 'amber'
                                  ? 'text-blue-700'
                                  : '',
                              )}
                            >
                              Coming soon
                            </span>
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="tablet:hidden flex justify-center gap-x-2">
          <Link
            href={`${process.env.NEXT_PUBLIC_CALENDLY_URL}`}
            className={cn(styles.link)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              data-test="Book a Demo-btn"
              className={cn(
                'h-9 w-full px-6 text-base font-semibold hover:no-underline',
                navbarMode === 'white' ? 'text-zinc-950' : 'text-white',
              )}
              variant="link"
            >
              Book a Demo
            </Button>
          </Link>
          <SignupLinkWithUTM>
            <Button
              data-test="Start Now-btn"
              className={cn(
                'h-9 w-full rounded-lg px-6 text-base font-semibold',
                navbarMode === 'dark' ? 'text-white' : 'text-black',
              )}
              variant={navbarMode === 'dark' ? 'primary' : 'secondary'}
            >
              Start Now
            </Button>
          </SignupLinkWithUTM>
        </div>
      </div>
      <div className="fixed inset-0 z-9 bg-black/50" onClick={setClose} />
    </>
  );
};
